import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import ReminderBell from '../components/ReminderBell';
import api, { formatApiErrorDetail } from '../api';
import { useAuth } from '../context/AuthContext';

/**
 * Launches list — upcoming / live / past. Phase 4.
 * Creators with a Clan can hit "Schedule a launch" to open the composer.
 */

const RELATIVE_FUTURE = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((d.getTime() - Date.now()) / 1000);
  if (s < 60) return 'starting now';
  const m = Math.floor(s / 60);
  if (m < 60) return `in ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `in ${h}h ${m % 60}m`;
  const dd = Math.floor(h / 24);
  return `in ${dd}d ${h % 24}h`;
};

export default function Launches() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filter, setFilter] = useState('upcoming');
  const [items, setItems] = useState(null);
  const [hasClan, setHasClan] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/clans/mine').then(({ data }) => setHasClan(!!data.clan)).catch(() => {});
  }, []);

  const load = (f) => {
    setItems(null);
    setErr('');
    // Phase 9.6 — new Clan-based Launches source
    const apiFilter = f === 'live' ? 'just_launched' : f === 'past' ? 'all' : 'upcoming';
    api.get(`/launches/clan-schedule?filter=${apiFilter}`)
      .then(({ data }) => setItems(data.launches || []))
      .catch((ex) => {
        setItems([]);
        setErr(formatApiErrorDetail(ex.response?.data?.detail) || 'Could not load launches.');
      });
  };

  useEffect(() => { load(filter); }, [filter]);

  const toggleInterest = async (launchId) => {
    try {
      const { data } = await api.post(`/launches/${launchId}/interest`);
      setItems((prev) => prev.map((x) => x.id === launchId
        ? { ...x, is_interested: data.is_interested, interested_count: data.interested_count }
        : x
      ));
    } catch { /* ignore */ }
  };

  return (
    <AppShell activeTab="launches">
      <div className="lch-wrap" data-testid="launches-page">
        <div className="lch-header">
          <div>
            <h1 className="lch-title">🚀 Launches</h1>
            <p className="lch-sub">Fresh Clans about to open — set a reminder and grab Bits the moment they go live.</p>
          </div>
          {/* Schedule CTA removed in Phase 9.6 — scheduling now happens
              inside the Create Clan wizard (Step 3). Legacy /schedule-launch
              route stays live but is no longer surfaced here. */}
          {hasClan && !user?.verified && (
            <button
              className="lch-schedule-btn lch-schedule-locked"
              onClick={() => navigate('/verify')}
              data-testid="lch-verify-to-schedule"
              title="Only verified creators can schedule launches"
            >
              🔒 Verify to schedule
            </button>
          )}
        </div>

        <div className="lch-tabs">
          {[
            { key: 'upcoming', label: 'Upcoming' },
            { key: 'live', label: 'Just launched' },
            { key: 'past', label: 'All' },
          ].map((t) => (
            <button
              key={t.key}
              className={`lch-tab${filter === t.key ? ' active' : ''}`}
              onClick={() => setFilter(t.key)}
              data-testid={`lch-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {err && <div className="notif-empty" style={{ color: 'var(--error)' }}>{err}</div>}

        {items === null && <div className="notif-loading" data-testid="lch-loading">loading…</div>}

        {items && items.length === 0 && (
          <div className="notif-empty" data-testid="lch-empty">
            <div className="notif-empty-emoji">🌱</div>
            <div className="notif-empty-title">
              {filter === 'upcoming' ? 'No upcoming launches' :
               filter === 'live' ? 'Nothing live right now' :
               filter === 'past' ? 'No past launches' : 'You haven\'t scheduled any launches'}
            </div>
            <p className="notif-empty-sub">
              {filter === 'mine' && hasClan
                ? 'Schedule your first launch to build hype for a drop.'
                : 'Check back soon — creators drop new sessions weekly.'}
            </p>
            {filter === 'mine' && hasClan && user?.verified && (
              <button className="mycl-cta" onClick={() => navigate('/schedule-launch')}>
                Schedule a launch
              </button>
            )}
            {filter === 'mine' && hasClan && !user?.verified && (
              <button className="mycl-cta" onClick={() => navigate('/verify')} data-testid="lch-mine-verify-cta">
                🔒 Verify to schedule launches
              </button>
            )}
          </div>
        )}

        {items && items.length > 0 && (
          <div className="lch-list" data-testid="lch-list">
            {items.map((l) => {
              const isLive = l.launch_at && new Date(l.launch_at) <= new Date();
              return (
              <div
                className="lch-card"
                key={l.handle}
                onClick={() => navigate(`/creator/${l.handle}`)}
                role="button"
                tabIndex={0}
                data-testid={`lch-card-${l.handle}`}
              >
                <div
                  className="lch-cover"
                  style={{ background: `linear-gradient(120deg, ${l.gradient[0]}, ${l.gradient[1]}, #E040FB)` }}
                >
                  <span className={`lch-status-badge lch-${isLive ? 'live' : 'scheduled'}`}>
                    {isLive ? <>🚀 JUST LAUNCHED</> : '🔜 ' + RELATIVE_FUTURE(l.launch_at)}
                  </span>
                  <div className="lch-cover-avatar" style={{ background: `linear-gradient(135deg, ${l.gradient[0]}, ${l.gradient[1]})` }}>
                    {l.initials}
                  </div>
                </div>
                <div className="lch-card-body">
                  <div className="lch-card-title">{l.display_name}</div>
                  <div className="lch-card-creator">@{l.creator_handle}</div>
                  <div className="lch-card-meta">
                    <span>💎 {l.current_price.toFixed(2)} pts / Bit</span>
                    {!isLive && (
                      <ReminderBell
                        handle={l.handle}
                        variant="chip"
                        initialCount={l.reminder_count || 0}
                        onCountChange={(newCount, newReminded) => {
                          setItems((prev) => prev.map((x) => x.handle === l.handle
                            ? { ...x, reminder_count: newCount, you_are_reminded: newReminded }
                            : x
                          ));
                        }}
                      />
                    )}
                    {isLive && (
                      <span>🔔 {l.reminder_count} reminded</span>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
